import React from 'react';
import { formatDate } from '../../utils/labels';
import CountryFlag from './CountryFlag';
import InfoHint from './InfoHint';
import PlayerCell from './PlayerCell';
import './AthleteHubRail.css';

const MOVEMENT_DISPLAY_COUNT = 6;
const QUALIFICATION_CUTOFF = 60;

const MiniSparkline = ({ points = [], tone = 'green' }) => {
  if (!points.length) return null;

  const width = 180;
  const height = 26;
  const ranks = points.map((point) => point.olympicRank);
  const minRank = Math.min(...ranks);
  const maxRank = Math.max(...ranks);
  const range = Math.max(1, maxRank - minRank);
  const xStep = points.length > 1 ? width / (points.length - 1) : width;
  const yForRank = (rank) => 3 + ((rank - minRank) / range) * (height - 6);
  const linePoints = points
    .map((point, idx) => `${idx * xStep},${yForRank(point.olympicRank)}`)
    .join(' ');

  return (
    <svg className={`hub-mini-chart hub-mini-chart--${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline points={linePoints} />
      {points.map((point, idx) => (
        <circle key={`${point.label}-${point.olympicRank}`} cx={idx * xStep} cy={yForRank(point.olympicRank)} r="2.3" />
      ))}
    </svg>
  );
};

const movementLabel = (mover) => {
  if (mover.isRiskOnly) return 'RISK';
  if (mover.isDirectionalOnly) return mover.delta > 0 ? 'UP' : 'DOWN';
  return mover.delta > 0 ? `+${mover.delta}` : `${mover.delta}`;
};

const MovementRow = ({ mover, tone, onPlayerClick }) => {
  return (
    <button type="button" className="hub-movement-row" onClick={() => onPlayerClick(mover.entry)}>
      <span className="hub-row-main">
        <PlayerCell
          firstName={mover.entry.firstName}
          lastName={mover.entry.lastName}
          country={mover.entry.country}
          headshotUrl={mover.entry.headshotUrl}
        />
        <span className="hub-row-meta">Rank {mover.endRank}</span>
      </span>
      <span className="hub-row-country">
        <CountryFlag code={mover.entry.country} size={16} />
        {mover.entry.country}
      </span>
      <span className="hub-row-move">
        <span className={`hub-delta hub-delta--${tone}`}>{movementLabel(mover)}</span>
      </span>
      <MiniSparkline points={mover.history.slice(-3)} tone={tone} />
    </button>
  );
};

const OutsideLineRow = ({ row, onPlayerClick }) => (
  <button type="button" className="hub-movement-row" onClick={() => onPlayerClick(row.entry)}>
    <span className="hub-row-main">
      <PlayerCell
        firstName={row.entry.firstName}
        lastName={row.entry.lastName}
        country={row.entry.country}
        headshotUrl={row.entry.headshotUrl}
      />
      <span className="hub-row-meta">Rank {row.endRank} · {row.endRank - QUALIFICATION_CUTOFF} outside the line</span>
    </span>
    <span className="hub-row-country">
      <CountryFlag code={row.entry.country} size={16} />
      {row.entry.country}
    </span>
    <span className="hub-row-move hub-row-rank">#{row.endRank}</span>
  </button>
);

const AthleteHubRail = ({
  biggestMovers = [],
  outsideLine = [],
  list,
  onPlayerClick,
}) => {
  const snapshotDate = list?.snapshotDate || list?.updatedAt || list?.createdAt;

  return (
    <aside className="athlete-hub-rail" aria-label="Rankings insights">
      <header className="athlete-hub-header">
        <p>ROAD TO LA</p>
        <span>{snapshotDate ? formatDate(snapshotDate) : 'Latest snapshot'}</span>
      </header>

      <section className="athlete-hub-section">
        <div className="athlete-hub-section-title">
          <span className="athlete-hub-section-title-main">
            <span>Biggest Movers</span>
            <InfoHint label="Biggest Movers">
              Largest Olympic-rank moves vs the prior published ranking, including climbs and drops across the field.
            </InfoHint>
          </span>
          <small>Largest Olympic-rank moves since the previous list</small>
        </div>
        <div className="hub-movement-list">
          {biggestMovers.length ? (
            biggestMovers.slice(0, MOVEMENT_DISPLAY_COUNT).map((mover) => (
              <MovementRow
                key={mover.key}
                mover={mover}
                tone={mover.delta > 0 ? 'green' : 'red'}
                onPlayerClick={onPlayerClick}
              />
            ))
          ) : (
            <p className="hub-empty">No major rank changes since the previous list.</p>
          )}
        </div>
      </section>

      <section className="athlete-hub-section">
        <div className="athlete-hub-section-title">
          <span className="athlete-hub-section-title-main">
            <span>Just Outside the Line</span>
            <InfoHint label="Just Outside the Line">
              Players immediately outside the Olympic qualification line in OGR order.
            </InfoHint>
          </span>
          <small>Players immediately outside the line</small>
        </div>
        <div className="hub-movement-list">
          {outsideLine.length ? (
            outsideLine.map((row) => (
              <OutsideLineRow key={row.key} row={row} onPlayerClick={onPlayerClick} />
            ))
          ) : (
            <p className="hub-empty">No athletes just outside the line in this window.</p>
          )}
        </div>
      </section>
    </aside>
  );
};

export default AthleteHubRail;
